import React, { useCallback, useEffect, useState } from 'react'
import {
  Loader2, CheckCircle2, AlertTriangle, Film, BadgeCheck, Tag, BarChart3, Tags, FileVideo,
  EyeOff, Eye, Maximize2, PieChart, Trophy, Calendar, MapPin, DollarSign
} from 'lucide-react'
import type { LucideIcon } from 'lucide-react'
import { Tooltip, KPICard } from '../ui'
import { useToast } from '../../contexts/ToastContext'
import broadcastAdminService, {
  BroadcastReview as Review, ExclusionReason, ReviewBrand, supportReviewSource
} from '../../services/broadcastAdminService'
import BroadcastBrandCard from './BroadcastBrandCard'
import BroadcastBrandDrawer from './BroadcastBrandDrawer'
import { secs } from './broadcastReviewConstants'
import { rateFor, valueOf, formatMoney } from './broadcastValuation'
import BroadcastToolbar from './BroadcastToolbar'
import BroadcastPagination from './BroadcastPagination'

// 24 = six full rows of the 4-wide grid at xl. The 3.1-hour run produced 85
// brands; a reviewer judging them one at a time needs a place to stop.
const PER_PAGE = 24

interface Props {
  customerId: string
  /**
   * The broadcast being reviewed, and the only one this panel will ever show.
   *
   * It used to carry a picker and fall back to "the first one awaiting
   * review", which made the page a second, worse version of the library: two
   * places to choose a broadcast, one of which silently disagreed with the
   * row you just clicked. The page that renders this has already chosen.
   */
  broadcastId: string
}

type SubTab = 'sponsored' | 'unsponsored' | 'analysis'

/**
 * Three tabs, two jobs.
 *
 * Sponsored and Unsponsored are DECISION surfaces: every brand the model
 * named, with the frame that proves or disproves it, and one control per
 * card. They are split because the rules differ, not the styling.
 *
 * Analysis detail is an EVIDENCE surface: coverage, scenes and cost — context
 * for the decision and for the methodology conversation, but nothing to act
 * on. It is named for what it holds rather than what it does, because a tab
 * reading "Run analysis" promises a button that is not there.
 *
 * They were one scrolling column before, which meant the reviewer scrolled past
 * charts to reach the only controls on the page.
 */
const BroadcastReviewPanel: React.FC<Props> = ({ customerId, broadcastId }) => {
  const { showSuccess, showError } = useToast()
  const [review, setReview] = useState<Review | null>(null)
  const [loading, setLoading] = useState(true)
  const [failed, setFailed] = useState(false)
  const [busy, setBusy] = useState(false)
  const [tab, setTab] = useState<SubTab>('sponsored')
  const [drilled, setDrilled] = useState<ReviewBrand | null>(null)
  const [search, setSearch] = useState('')
  const [page, setPage] = useState(1)
  const [refreshing, setRefreshing] = useState(false)
  // Default to what actually counts. A reviewer opening a 184-brand run wants
  // the sponsor list, not the league and ten clubs off a standings graphic.
  const [onlyCounted, setOnlyCounted] = useState(true)
  const [classifying, setClassifying] = useState(false)

  // Switching broadcast or narrowing the set invalidates the page number.
  useEffect(() => { setPage(1) }, [search, broadcastId, onlyCounted, tab])

  const loadReview = useCallback(async (id: string) => {
    try {
      const res = await broadcastAdminService.getReview(customerId, id)
      setReview(res.data)
      setFailed(false)
    } catch (err) {
      setFailed(true)
      showError('Failed to load review', err instanceof Error ? err.message : 'Please try again')
    }
  }, [customerId, showError])

  /**
   * Re-match every brand against league, team and venue records. Costs
   * nothing — no frames, no model — which is why it is a plain button and not
   * behind a confirmation like Analyse.
   *
   * The case it exists for: a broadcast analysed before its competition was
   * seeded has every club sitting in the sponsor list. Seed the reference
   * data, press this, done. Reviewer decisions are never overwritten.
   */
  const reclassify = async () => {
    setClassifying(true)
    try {
      const res = await broadcastAdminService.reclassifyBroadcast(customerId, broadcastId)
      const n = res.data?.classified ?? 0
      const by = Object.entries(res.data?.byType ?? {})
        .map(([k, v]) => `${v} ${k}`).join(', ')
      showSuccess(
        n ? `Classified ${n} brand${n === 1 ? '' : 's'}` : 'Nothing to classify',
        n ? by : 'No detected brand matched a league, team or venue record.'
      )
      await loadReview(broadcastId)
    } catch (err) {
      showError('Could not classify',
        err instanceof Error ? err.message : 'Please try again')
    } finally { setClassifying(false) }
  }

  const refresh = async () => {
    setRefreshing(true)
    try { await loadReview(broadcastId) } finally { setRefreshing(false) }
  }

  useEffect(() => {
    let live = true
    setLoading(true)
    void loadReview(broadcastId).finally(() => { if (live) setLoading(false) })
    return () => { live = false }
  }, [broadcastId, loadReview])

  const toggleExclude = async (brand: string, reason: ExclusionReason | null) => {
    setBusy(true)
    try {
      await broadcastAdminService.setBrandExclusion(customerId, broadcastId, brand, reason)
      await loadReview(broadcastId)
    } catch (err) {
      showError('Could not update', err instanceof Error ? err.message : 'Please try again')
    } finally { setBusy(false) }
  }

  const publish = async () => {
    setBusy(true)
    try {
      await broadcastAdminService.publishBroadcast(customerId, broadcastId)
      showSuccess('Published', 'The customer can now see this broadcast.')
      await loadReview(broadcastId)
    } catch (err) {
      showError('Could not publish', err instanceof Error ? err.message : 'Please try again')
    } finally { setBusy(false) }
  }

  if (loading) {
    return <div className="flex items-center justify-center py-16 text-gray-500">
      <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading…
    </div>
  }

  // One broadcast, so "nothing to review" is no longer a state this panel can
  // be in - the list upstream decides that. What it CAN be is pointed at a
  // broadcast that has since been archived, or at an id typed into the URL.
  if (failed || !review) {
    return <div className="bg-white rounded-lg border border-gray-200 py-14 text-center">
      <Film className="h-8 w-8 text-gray-300 mx-auto mb-2" />
      <p className="text-sm font-medium text-gray-600">This broadcast could not be loaded</p>
      <p className="text-xs text-gray-400 mt-1">
        It may have been archived, or it has not been analysed yet.
      </p>
    </div>
  }

  const allBrands = review?.brands ?? []

  /**
   * The one split that matters, and the server already made it: is_sponsored
   * is resolved from the ACTIVE partner list on every read, so these two
   * populations move the instant a partner is added, aliased, soft-deleted or
   * restored. Nothing here recomputes it.
   *
   * They are separate tabs because the rules diverge, not merely the styling.
   * A sponsored brand cannot be excluded and its sponsor cannot be reassigned
   * - it is inventory somebody paid for. An unsponsored one is the opposite:
   * judging it IS the review. Mixing them put a column of controls next to
   * rows that must never use them.
   *
   * "Unsponsored", not "marks": mark is a trademark term and a CRO reading
   * this page has never needed it. It survives on the per-card type badges -
   * competition, club, venue, broadcaster - where it is precise, and in
   * mark_type in the schema. It cannot carry a whole tab. Plain "Brands" was
   * the other candidate and is worse: sponsored rows are brands too, so it
   * would make "how many brands did we detect" unanswerable from the tabs.
   */
  const sponsoredBrands = allBrands.filter((b) => b.is_sponsored)
  const unsponsoredBrands = allBrands.filter((b) => !b.is_sponsored)
  const isBrandTab = tab === 'sponsored' || tab === 'unsponsored'
  const tabBrands = tab === 'sponsored' ? sponsoredBrands : unsponsoredBrands

  // Counted-only is an UNSPONSORED control. It exists to push leagues, clubs
  // and broadcaster idents out of the way, and the sponsored list is short and
  // entirely relevant, so filtering it would only hide things.
  const notCounted = unsponsoredBrands.filter((b) => !b.counts).length
  // Searches the RAW spellings too: a reviewer chasing "felton" should find the
  // card whether or not the resolver already snapped it to "Felton Industries".
  const q = search.trim().toLowerCase()
  const base = tab === 'unsponsored' && onlyCounted
    ? tabBrands.filter((b) => b.counts)
    : tabBrands
  const shown = q
    ? base.filter((b) =>
        // display_name as well as brand_name: searching "Derringer" must find
        // the card that now reads Derringer, not only the raw "derringer".
        (b.display_name ?? b.brand_name).toLowerCase().includes(q) ||
        b.brand_name.toLowerCase().includes(q) ||
        (b.raw_names ?? []).some((r) => String(r).toLowerCase().includes(q)))
    : base
  const pageBrands = shown.slice((page - 1) * PER_PAGE, page * PER_PAGE)

  // The server decides what counts — it folds in is_own, exclusion and the
  // mark rule. Re-deriving it here is how the page and the totals drift.
  const counted = allBrands.filter((b) => b.counts)
  const areaOf = (xs: ReviewBrand[]) =>
    xs.reduce((sum, b) => sum + Number(b.total_area_seconds), 0)

  // The COUNT cards show the same populations as the tabs, so a KPI never
  // contradicts the tab label two inches below it. The AREA cards are
  // counted-only, because that is the figure value is derived from - and
  // being counted on both sides, they still sum to the reported total.
  const sponsoredCounted = sponsoredBrands.filter((b) => b.counts)
  const unsponsoredCounted = unsponsoredBrands.filter((b) => b.counts)
  const sponsoredArea = areaOf(sponsoredCounted)
  const unsponsoredArea = areaOf(unsponsoredCounted)
  // Everything that counts, which is exactly the two above - the share is a
  // share of the reported total, not of everything detected.
  const countedArea = sponsoredArea + unsponsoredArea

  /**
   * One rate for the whole broadcast, read off the server rather than
   * recomputed — rate_per_30s is already audience / 1000 x the resolved CPM,
   * and a second implementation here would be a second rounding rule.
   *
   * Null is the ORDINARY state: audience lands weeks after an event, so most
   * broadcasts reach review unpriced. Everything below renders a dash for it
   * rather than zero.
   */
  const rate = rateFor(review.broadcast)
  /**
   * The same figure the customer will see on their Exposure tab, so the
   * reviewer is not approving a number they have never been shown.
   *
   * Null when frames were never recorded. Zero would claim no sponsor was
   * ever on screen, which is a measurement nobody made.
   */
  const presence = review.frames && review.frames.analysed > 0
    ? review.frames.withSponsored / review.frames.analysed
    : null
  const sponsoredValue = valueOf(sponsoredArea, rate).value
  const unsponsoredValue = valueOf(unsponsoredArea, rate).value
  const isPublished = review?.broadcast.status === 'published'
  // Frames where SOMETHING was detected — not frames analysed. On the first
  // real run that was 272 of 600, and conflating the two is exactly what made
  // the cost estimator 73% high.
  const framesWithDetections = review.frames?.withDetections
    ?? review.scenes.reduce((s, x) => s + x.frames, 0)
  const meta = review.broadcast.metadata ?? null
  // broadcast_frames is what was actually written; metadata.frames_analysed
  // is what the run said about itself. Prefer the table, so the presence
  // percentage above and this count cannot disagree on one page.
  const framesAnalysed = review.frames?.analysed ?? meta?.frames_analysed ?? null

  // Only what is actually present. A blank chip reads as missing data; an
  // absent one reads as "not applicable", which is usually the truth - a
  // friendly has no competition stage, a studio feed has no venue.
  const bc = review.broadcast
  /**
   * Three chips, three SIMPLE glyphs.
   *
   * Lucide draws on a 24px grid with a 2px stroke, so at 12px the strokes
   * land on half-pixels and the denser icons lose parts of themselves -
   * CalendarDays, Cpu and Hash all have interior detail that collapses into
   * smudge. Fixed by drawing at 14px with a 1.75 stroke AND by not asking a
   * tiny icon to carry fine detail in the first place.
   *
   * Season rides with the league rather than taking a fourth chip: "ABL
   * 2026-27" is how the competition is actually referred to, and it avoids a
   * second calendar icon sitting next to the first.
   *
   * Competition stage is deliberately absent: the generated title is
   * "<away> @ <home> - <stage>, <date>", so it would repeat the line above.
   */
  const matchBits = [
    bc.league_short_name && {
      Icon: Trophy,
      label: [bc.league_short_name, bc.season].filter(Boolean).join(' ')
    },
    bc.event_date && {
      Icon: Calendar,
      // Date-only, so parse as UTC and print as UTC. Left to the local
      // timezone a 2026-09-28 fixture shows as the 27th west of Greenwich.
      label: new Date(`${String(bc.event_date).slice(0, 10)}T00:00:00Z`)
        .toLocaleDateString(undefined, { dateStyle: 'medium', timeZone: 'UTC' })
    },
    bc.venue_name && { Icon: MapPin, label: bc.venue_name }
  ].filter(Boolean) as { Icon: LucideIcon; label: string }[]

  /**
   * No icons at all on the run line. A 12px dollar sign and a 12px hash were
   * the worst offenders, and they were also the least informative: "$ 12.40"
   * and "# v3" both need decoding, where a word does not.
   */
  const runBits = [
    bc.detection_model && { k: 'model', v: bc.detection_model },
    bc.prompt_version && { k: 'prompt', v: bc.prompt_version },
    bc.actual_cost_usd != null && {
      k: 'cost', v: `$${Number(bc.actual_cost_usd).toFixed(2)}`
    }
  ].filter(Boolean) as { k: string; v: string }[]

  /**
   * How this broadcast is priced, for the header.
   *
   * A reviewer approving dollar figures needs the inputs behind them in
   * reach, and "open Valuation inputs to check" is a round trip away from
   * the decision. The line carries the three numbers that matter; the
   * tooltip carries the provenance that only matters when something looks
   * wrong.
   */
  const ccy = bc.effective_currency ?? bc.currency ?? 'USD'
  const cpmText = bc.effective_cpm == null
    ? null
    : `${Number(bc.effective_cpm).toFixed(2)} ${ccy} CPM`
  const basisLabel = bc.valuation_basis === 'negotiated_cpm'
    ? 'Negotiated rate'
    : bc.valuation_basis === 'rate_card' ? 'Customer rate card' : null

  const pricingDetail = (
    <div className="space-y-1 text-left">
      {[
        ['Basis', basisLabel ?? 'Not priced'],
        ['Rate card', bc.rate_card_name ?? (bc.valuation_basis === 'negotiated_cpm'
          ? 'Overridden for this broadcast' : 'None assigned')],
        ['CPM', cpmText ?? '—'],
        ['Audience', bc.viewers == null
          ? 'Not recorded'
          : `${Number(bc.viewers).toLocaleString()}${bc.viewership_source_name
              ? ` · ${bc.viewership_source_name}${bc.viewership_is_estimate ? ' (estimate)' : ''}`
              : ''}`],
        ['Feed Provider', bc.channel_name ?? '—'],
        ['30-second rate', rate === null ? '—' : formatMoney(rate, ccy)]
      ].map(([k, v]) => (
        <div key={k} className="flex gap-3 justify-between">
          <span className="text-gray-400">{k}</span>
          <span className="font-medium text-right">{v}</span>
        </div>
      ))}
    </div>
  )

  const TABS: { id: SubTab; label: string; icon: React.ReactNode }[] = [
    { id: 'sponsored', label: `Sponsored (${sponsoredBrands.length})`, icon: <BadgeCheck className="h-3.5 w-3.5" /> },
    { id: 'unsponsored', label: `Unsponsored (${unsponsoredBrands.length})`, icon: <Tag className="h-3.5 w-3.5" /> },
    // "Analysis detail", not "Run analysis": the latter is a verb in
    // imperative position, sitting on a page that does have action buttons,
    // so it read as the place you go to START an analysis. Nothing here runs
    // anything. Not bare "Analysis" either - the whole page is an analysis;
    // "detail" says this is the supporting material behind the two tabs
    // beside it.
    { id: 'analysis', label: 'Analysis detail', icon: <BarChart3 className="h-3.5 w-3.5" /> }
  ]

  return (
    <div className="space-y-4">
      {review && (
        <>
          {/* Identity first, numbers second.
              Match info and the run's provenance sit ABOVE the KPIs because
              they answer "which broadcast am I looking at" - the question a
              reviewer asks before any number means anything. The run line is
              kept visually quieter than the match line: the model and prompt
              version matter when a result is disputed, not while judging it. */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <div className="flex items-start justify-between gap-4">
              <div className="min-w-0">
                <h2 className="text-xl font-bold text-gray-900 leading-snug">
                  {review.broadcast.title}
                </h2>

                <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-gray-600">
                  {matchBits.map((m) => (
                    <span key={m.label} className="inline-flex items-center gap-1">
                      <m.Icon className="h-3.5 w-3.5 text-gray-500 flex-shrink-0" strokeWidth={1.75} />
                      {m.label}
                    </span>
                  ))}
                </div>

                {/* Pricing sits between the fixture and the run detail, and
                    reads louder than either: every figure on this page is
                    derived from it, and a wrong CPM is a wrong report. */}
                <div className="mt-2">
                  <Tooltip content={pricingDetail} placement="bottom" maxWidth={300}>
                    <span className={`inline-flex items-center gap-1.5 text-xs cursor-help rounded px-1.5 py-0.5 -ml-1.5 ${
                      rate === null
                        ? 'text-amber-700 bg-amber-50 hover:bg-amber-100'
                        : 'text-emerald-800 bg-emerald-50 hover:bg-emerald-100'
                    }`}>
                      <DollarSign className="h-3.5 w-3.5 flex-shrink-0" strokeWidth={1.75} />
                      {rate === null ? (
                        <>Not priced{bc.viewers == null && cpmText
                          ? ' — no audience recorded'
                          : bc.viewers != null && !cpmText ? ' — no rate card assigned' : ''}</>
                      ) : (
                        <>
                          <span className="font-medium">{bc.rate_card_name ?? 'Negotiated rate'}</span>
                          <span className="text-gray-400">·</span>{cpmText}
                          <span className="text-gray-400">·</span>
                          {Number(bc.viewers).toLocaleString()} viewers
                          <span className="text-gray-400">·</span>
                          <span className="font-semibold">{formatMoney(rate, ccy)} per :30</span>
                        </>
                      )}
                    </span>
                  </Tooltip>
                </div>

                <div className="mt-1.5 flex flex-wrap items-center gap-x-2 gap-y-1 text-[11px] text-gray-400">
                  {runBits.map((r, i) => (
                    <span key={r.k} className="inline-flex items-center gap-1">
                      {i > 0 && <span className="text-gray-300 mr-1">·</span>}
                      <span>{r.k}</span>
                      <span className="font-mono text-gray-500">{r.v}</span>
                    </span>
                  ))}
                </div>
              </div>

              {isPublished ? (
                <span className="px-2 py-1 rounded-md text-xs font-semibold bg-emerald-100 text-emerald-700 whitespace-nowrap">
                  Published
                </span>
              ) : (
                <button onClick={publish} disabled={busy}
                  className="inline-flex items-center flex-shrink-0 px-3 py-1.5 bg-gradient-to-r from-emerald-600 to-emerald-700 text-white text-xs font-medium rounded-lg hover:from-emerald-700 hover:to-emerald-800 disabled:opacity-50 transition-all shadow-sm">
                  <CheckCircle2 className="h-3.5 w-3.5 mr-1.5" />Publish to customer
                </button>
              )}
            </div>
          </div>

          <div className="border-b border-gray-200 flex gap-1">
            {TABS.map((t) => (
              <button
                key={t.id}
                onClick={() => setTab(t.id)}
                className={`inline-flex items-center gap-1.5 px-3 py-2 text-xs font-medium border-b-2 -mb-px transition-colors ${
                  tab === t.id
                    ? 'border-blue-600 text-blue-700'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                {t.icon}{t.label}
              </button>
            ))}
          </div>

          {isBrandTab && (
            <>
              {/* Three cards, on the tab they describe.
                  Six above the tabs forced a reader to work out which three
                  applied to what they were looking at, and the two
                  "area-sec" cards sat in different rows. Here the tab
                  supplies the noun, so the labels lose their prefixes and
                  the whole row is about one population.

                  Value leads. It is the figure the page exists to produce
                  and the one a reviewer is accountable for; area-seconds is
                  the working behind it. */}
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                {tab === 'sponsored' ? (
                  <>
                    <KPICard
                      label="Value"
                      value={formatMoney(sponsoredValue)}
                      icon={DollarSign}
                      colorScheme="emerald"
                      tooltip="Sponsored area-seconds priced at this broadcast's 30-second rate. A dash means the broadcast is not priced yet — it needs an audience and a rate card on Valuation inputs. Never shown as zero, because zero is a measurement and 'not told yet' is not."
                    />
                    <KPICard
                      label="Area-seconds"
                      value={sponsoredArea.toFixed(2)}
                      icon={Maximize2}
                      colorScheme="emerald"
                      tooltip="Screen area x time for sponsored brands that count, in full-screen-seconds. This is the figure sponsor value is derived from."
                    />
                    <KPICard
                      label="Sponsor on screen"
                      value={presence === null ? '—' : `${(presence * 100).toFixed(0)}%`}
                      icon={Eye}
                      colorScheme="emerald"
                      tooltip="Share of the frames analysed in which at least one sponsored brand was visible. Counted once per frame however many sponsors are in shot, so it never exceeds 100% — a different question from area-seconds, which asks how much screen they filled."
                    />
                    <KPICard
                      label="Share of exposure"
                      value={countedArea > 0 ? `${((sponsoredArea / countedArea) * 100).toFixed(0)}%` : '—'}
                      icon={PieChart}
                      colorScheme="emerald"
                      tooltip="Sponsored area-seconds as a share of everything that counts on this broadcast. The honest read on how much of the exposure was inventory somebody paid for. Shown as a dash when nothing counts yet."
                    />
                  </>
                ) : (
                  <>
                    <KPICard
                      label="Value"
                      value={formatMoney(unsponsoredValue)}
                      icon={DollarSign}
                      colorScheme="blue"
                      tooltip="What the counted unsponsored exposure would be worth at this broadcast's rate. Nobody is paying it — this is the scale of the gap, not revenue."
                    />
                    <KPICard
                      label="Brands"
                      value={unsponsoredBrands.length}
                      icon={Tag}
                      colorScheme="blue"
                      tooltip="Brands matching no active partner. Most do not count toward the customer's numbers - leagues, clubs, venues and broadcaster idents never do - so this is usually far larger than the area figure beside it implies."
                    />
                    <KPICard
                      label="Area-seconds"
                      value={unsponsoredArea.toFixed(2)}
                      icon={Maximize2}
                      colorScheme="blue"
                      tooltip="Screen area x time for unsponsored brands that count - normally those on this customer's brand-hint list. Leagues, clubs, venues and broadcaster idents contribute nothing."
                    />
                    <KPICard
                      label="Excluded"
                      value={allBrands.length - counted.length}
                      icon={EyeOff}
                      colorScheme="amber"
                      tooltip="Detected and kept on record, but hidden from the customer's numbers. Nothing is deleted. Sponsored brands cannot be excluded, so every one of these is unsponsored."
                    />
                  </>
                )}
              </div>

              <BroadcastToolbar
                value={search}
                onChange={setSearch}
                placeholder="Search brands or raw spellings..."
                onRefresh={() => { void refresh() }}
                refreshing={refreshing}
                refreshLabel="Reload review"
              >
                {/* An UNSPONSORED-tab action. All it does is label leagues,
                    clubs, venues and broadcaster idents from reference data so
                    they stop sitting in the counted set. It cannot make
                    anything sponsored or unsponsored - that is resolved from
                    the partner list - so on the Sponsored tab it was a button
                    that could not affect a single row in front of you. */}
                {tab === 'unsponsored' && <Tooltip
                  content="Re-label leagues, clubs, venues and broadcaster idents from reference data, so they drop out of the counted set. Free — no frames, no model, and reviewer decisions are never overwritten."
                  placement="top"
                  maxWidth={260}
                >
                  <button
                    onClick={() => { void reclassify() }}
                    disabled={classifying || isPublished}
                    className="inline-flex items-center px-3 py-1.5 text-xs font-medium border rounded-lg transition-colors bg-white text-gray-700 border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {classifying
                      ? <><Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />Classifying…</>
                      : <><Tags className="h-3.5 w-3.5 mr-1.5" />Reclassify</>}
                  </button>
                </Tooltip>}
                {tab === 'unsponsored' && <button
                  onClick={() => setOnlyCounted((v) => !v)}
                  className={`inline-flex items-center px-3 py-1.5 text-xs font-medium border rounded-lg transition-colors ${
                    onlyCounted
                      ? 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                      : 'bg-blue-50 text-blue-700 border-blue-200'
                  }`}
                >
                  {onlyCounted
                    ? `Show all (${notCounted} not counted)`
                    : 'Counted only'}
                </button>}
              </BroadcastToolbar>
              <div className="grid gap-3 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                {pageBrands.map((b) => (
                  <BroadcastBrandCard
                    key={b.brand_key}
                    brand={b}
                    customerId={customerId}
                    broadcastId={broadcastId}
                    rate={rate}
                    disabled={busy || isPublished}
                    source={supportReviewSource}
                    onOpen={() => setDrilled(b)}
                    onExclude={(reason) => toggleExclude(b.brand_name, reason as ExclusionReason | null)}
                  />
                ))}
              </div>

              {shown.length === 0 && (
                <div className="bg-white rounded-lg border border-gray-200 py-10 text-center">
                  {q ? (
                    <p className="text-xs text-gray-400">No brands match “{search}”</p>
                  ) : tab === 'sponsored' ? (
                    <>
                      <p className="text-sm font-medium text-gray-600">No sponsors detected</p>
                      <p className="text-xs text-gray-400 mt-1 max-w-md mx-auto">
                        A brand becomes a sponsor by matching an active partner's name or
                        one of its aliases. Add the partner, or add the detected spelling
                        as an alias, and it moves here on the next load.
                      </p>
                    </>
                  ) : (
                    <p className="text-xs text-gray-400">
                      No unsponsored brands{onlyCounted ? ' that count' : ''}
                    </p>
                  )}
                </div>
              )}

              <BroadcastPagination
                page={page}
                perPage={PER_PAGE}
                total={shown.length}
                unfilteredTotal={tabBrands.length}
                noun="brands"
                onPage={setPage}
              />

              {!isPublished && (
                <p className="text-xs text-gray-500 flex items-start gap-1.5">
                  <AlertTriangle className="h-3.5 w-3.5 flex-shrink-0 mt-0.5 text-amber-500" />
                  Nothing here reaches the customer until published. Excluded brands stay recorded —
                  they are hidden from the customer's numbers, not deleted.
                </p>
              )}
            </>
          )}

          {tab === 'analysis' && (
            <div className="space-y-4">
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
                <div className="flex items-baseline justify-between gap-3 flex-wrap mb-3">
                  <h4 className="text-sm font-semibold text-gray-700">Run detail</h4>
                  {/* The source file, because by the time a run is reviewed the
                      title has been edited and nobody can tell which upload
                      produced these numbers — which matters most when two runs
                      of the same game are being compared. */}
                  {review.broadcast.source_filename && (
                    <span
                      title={review.broadcast.source_filename}
                      className="inline-flex items-center gap-1.5 text-[11px] text-gray-500 font-mono max-w-full truncate"
                    >
                      <FileVideo className="h-3 w-3 flex-shrink-0 text-gray-400" />
                      {review.broadcast.source_filename}
                    </span>
                  )}
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-y-3 gap-x-4 text-xs">
                  {[
                    ['Duration', secs(review.broadcast.duration_ms ?? 0)],
                    ['Frames analysed', framesAnalysed ? framesAnalysed.toLocaleString() : '—'],
                    ['Frames with detections', framesWithDetections
                      ? framesWithDetections.toLocaleString() : '—'],
                    ['Appearances', review.brands.reduce((s, b) => s + b.appearances, 0).toLocaleString()],
                    ['Model', review.broadcast.detection_model ?? '—'],
                    ['Prompt version', review.broadcast.prompt_version ?? '—'],
                    ['Estimated cost', review.broadcast.estimated_cost_usd != null
                      ? `$${Number(review.broadcast.estimated_cost_usd).toFixed(2)}` : '—'],
                    ['Actual cost', review.broadcast.actual_cost_usd != null
                      ? `$${Number(review.broadcast.actual_cost_usd).toFixed(2)}` : '—'],
                    ['Credits charged', review.broadcast.credits_charged != null
                      ? String(review.broadcast.credits_charged) : '—'],
                    ['Started', review.broadcast.analysis_started_at
                      ? new Date(review.broadcast.analysis_started_at).toLocaleString() : '—'],
                    ['Finished', review.broadcast.analysis_ended_at
                      ? new Date(review.broadcast.analysis_ended_at).toLocaleString() : '—']
                  ].map(([k, v]) => (
                    <div key={k}>
                      <p className="text-gray-400 uppercase tracking-wider text-[10px] font-semibold">{k}</p>
                      <p className="font-medium text-gray-900 truncate" title={v}>{v}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Coverage — what was present but unnameable. A reviewer judging
                  only what we DID read has no sense of the gap. */}
              {review.coverage.length > 0 && (
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
                  <h4 className="text-sm font-semibold text-gray-700 mb-2">Signage coverage by placement</h4>
                  <div className="space-y-1.5">
                    {review.coverage.filter((c) => c.unreadable > 0).map((c) => {
                      const total = c.named + c.unreadable
                      return (
                        <div key={c.zone_code} className="flex items-center gap-2 text-xs">
                          {/* Label AND code: the label is the word customers see,
                              the code is what a reviewer corrects against. */}
                          <span className="w-52 truncate" title={`${c.zone_label ?? c.zone_code} (${c.zone_code})`}>
                            <span className="text-gray-600">{c.zone_label ?? c.zone_code}</span>
                            {c.zone_label && (
                              <span className="text-gray-400 font-mono text-[10px] ml-1.5">{c.zone_code}</span>
                            )}
                          </span>
                          <div className="flex-1 h-2.5 bg-gray-100 rounded-full overflow-hidden flex">
                            <div className="h-full bg-emerald-500"
                                 style={{ width: `${100 - c.pct_unreadable}%` }}
                                 title={`${c.named} read`} />
                            <div className="h-full bg-amber-500"
                                 style={{ width: `${c.pct_unreadable}%` }}
                                 title={`${c.unreadable} unreadable`} />
                          </div>
                          <span className="w-44 text-right text-gray-600 tabular-nums">
                            <span className="font-medium">{c.unreadable}</span> of {total} unreadable
                            <span className="text-gray-400"> ({c.pct_unreadable}%)</span>
                          </span>
                        </div>
                      )
                    })}
                  </div>
                  <div className="flex items-center gap-3 text-xs text-gray-500 mt-3">
                    <span className="flex items-center gap-1">
                      <span className="inline-block w-3 h-2 rounded-sm bg-emerald-500" /> read and attributed
                    </span>
                    <span className="flex items-center gap-1">
                      <span className="inline-block w-3 h-2 rounded-sm bg-amber-500" /> present but unreadable
                    </span>
                  </div>
                </div>
              )}

              {/* What the camera was actually showing. Phase 0 found a
                  rain-delayed game was ~60% non-play content, which is the
                  difference between a fair meter and an unfair one. */}
              {review.scenes.length > 0 && (
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
                  <h4 className="text-sm font-semibold text-gray-700 mb-2">What was on screen</h4>
                  <div className="space-y-1.5">
                    {review.scenes.map((s) => {
                      const share = framesWithDetections
                        ? Math.round((s.frames / framesWithDetections) * 100) : 0
                      return (
                        <div key={s.scene} className="flex items-center gap-2 text-xs">
                          <span className="w-40 text-gray-600 truncate">{s.scene.replace(/_/g, ' ')}</span>
                          <div className="flex-1 h-2.5 bg-gray-100 rounded-full overflow-hidden">
                            <div className="h-full bg-blue-500" style={{ width: `${share}%` }} />
                          </div>
                          <span className="w-44 text-right text-gray-600 tabular-nums">
                            <span className="font-medium">{s.frames.toLocaleString()}</span> frames
                            <span className="text-gray-400"> ({share}%)</span>
                          </span>
                        </div>
                      )
                    })}
                  </div>
                  <p className="text-[11px] text-gray-400 mt-2">
                    Shares are of the {framesWithDetections.toLocaleString()} frames that
                    contained at least one detection
                    {framesAnalysed ? `, out of ${framesAnalysed.toLocaleString()} analysed` : ''}.
                    Frames where nothing was found carry no scene and are not shown.
                  </p>
                </div>
              )}

            </div>
          )}
        </>
      )}

      <BroadcastBrandDrawer
        brand={drilled}
        // Only brands that count: merging a sponsor into an excluded row or a
        // club mark is a mistake the list should not offer.
        candidates={allBrands.filter((b) => b.counts).map((b) => ({
          // Labelled with display_name so the picker and the cards agree.
          // The VALUE is still brand_key, so what gets merged is unchanged.
          brand_key: b.brand_key, brand_name: b.display_name ?? b.brand_name
        }))}
        customerId={customerId}
        broadcastId={broadcastId}
        readOnly={isPublished}
        source={supportReviewSource}
        onZoneChanged={() => { void loadReview(broadcastId) }}
        onClose={() => setDrilled(null)}
      />
    </div>
  )
}

export default BroadcastReviewPanel
